@if (error(); as e) {
  <div class="alert alert-warning">{{ ('companion.errors.' + e) | translate }}</div>
}

<!-- Today's state -->
<div class="cp-card">
  <div class="cp-row">
    <h2 class="cp-h mb-0">{{ 'companion.today.title' | translate:{ date: (today().date | date:'fullDate':undefined:lang()) } }}</h2>
    <span class="cp-chip" [class.cp-chip--ok]="today().state === 'confirmed'">{{ ('companion.today.state.' + today().state) | translate }}</span>
  </div>
  @if (today().present_kinds.length) {
    <p class="cp-hint mt-2 mb-0">
      {{ 'companion.today.recorded' | translate }}:
      @for (k of today().present_kinds; track k) { <span class="cp-chip">{{ ('companion.kinds.' + k) | translate }}</span> }
    </p>
  } @else {
    <p class="cp-hint mt-2 mb-0">{{ 'companion.today.nothing_yet' | translate }}</p>
  }
</div>

<!-- Plan card -->
@if (confirmed(); as plan) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.plan.confirmed_title' | translate }}</h2>
    @if (plan.summary) { <p class="mb-2">{{ plan.summary }}</p> }
    <ul class="cp-steps">
      @for (s of plan.steps; track $index; let i = $index) {
        <li>
          <input type="checkbox" class="form-check-input mt-1" [checked]="reviewDone().includes(i)" (change)="toggleDone(i)" [disabled]="readOnly()" />
          <span class="cp-area">{{ ('companion.plan.areas.' + s.area) | translate }}</span>
          <span>{{ s.text }}</span>
        </li>
      }
    </ul>
    @if (plan.rationale) { <p class="cp-hint mt-2">{{ plan.rationale }}</p> }
    @if (!readOnly()) {
      <label class="cp-field mt-2">
        <span>{{ 'companion.plan.review_notes' | translate }}</span>
        <textarea class="form-control" rows="2" [ngModel]="reviewNotes()" (ngModelChange)="reviewNotes.set($event)"></textarea>
      </label>
      <div class="cp-actions">
        <button cButton color="primary" size="sm" [disabled]="busy()" (click)="saveReview(plan)">{{ 'companion.plan.save_review' | translate }}</button>
        @if (aiOn()) {
          <button cButton color="secondary" variant="outline" size="sm" [disabled]="busy() || pending()" (click)="changeMode.set(true)">{{ 'companion.plan.change' | translate }}</button>
        }
      </div>
    }
  </div>
} @else if (draft(); as plan) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.plan.draft_title' | translate }}</h2>
    @if (plan.summary) { <p class="mb-2">{{ plan.summary }}</p> }
    <ul class="cp-steps">
      @for (s of plan.steps; track $index) {
        <li><span class="cp-area">{{ ('companion.plan.areas.' + s.area) | translate }}</span><span>{{ s.text }}</span></li>
      }
    </ul>
    @if (plan.rationale) { <p class="cp-hint mt-2">{{ plan.rationale }}</p> }
    @if (plan.question) { <p class="mt-2"><strong>{{ plan.question }}</strong></p> }
    @if (!readOnly()) {
      <div class="cp-actions">
        <button cButton color="primary" [disabled]="busy()" (click)="confirmPlan(plan)">{{ 'companion.plan.accept' | translate }}</button>
        <button cButton color="secondary" variant="outline" [disabled]="busy() || pending()" (click)="changeMode.set(true)">{{ 'companion.plan.change' | translate }}</button>
        <button cButton color="secondary" variant="ghost" [disabled]="busy()" (click)="declinePlan(plan)">{{ 'companion.plan.decline' | translate }}</button>
      </div>
      <p class="cp-hint mt-2 mb-0">{{ 'companion.plan.draft_hint' | translate }}</p>
    }
  </div>
} @else if (!readOnly() && aiOn()) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.plan.none_title' | translate }}</h2>
    <p class="cp-hint">{{ 'companion.plan.none_hint' | translate }}</p>
    @if (lastPlan(); as lp) {
      <p class="cp-hint">{{ 'companion.plan.last_plan' | translate:{ date: (lp.reference_date | date:'mediumDate':undefined:lang()) } }}: {{ lp.summary }}</p>
    }
    <button cButton color="primary" [disabled]="busy() || pending()" (click)="propose()">{{ 'companion.plan.propose' | translate }}</button>
  </div>
}

@if (changeMode()) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.plan.change_title' | translate }}</h2>
    <textarea class="form-control" rows="2" [placeholder]="'companion.plan.change_placeholder' | translate"
              [ngModel]="changeText()" (ngModelChange)="changeText.set($event)"></textarea>
    <div class="cp-actions">
      <button cButton color="primary" size="sm" [disabled]="busy() || !changeText().trim()" (click)="submitChange()">{{ 'companion.plan.change_submit' | translate }}</button>
      <button cButton color="secondary" variant="ghost" size="sm" (click)="changeMode.set(false)">{{ 'companion.cancel' | translate }}</button>
    </div>
  </div>
}

<!-- Conversation -->
<div class="cp-card">
  <h2 class="cp-h">{{ 'companion.chat.title' | translate }}</h2>
  @if (loadingMessages()) {
    <div class="text-center py-2"><c-spinner size="sm" /></div>
  } @else if (!messages().length) {
    <p class="cp-hint">{{ 'companion.chat.empty' | translate }}</p>
  }
  <div class="chat" aria-live="polite">
    @for (m of messages(); track m.id) {
      <div class="bubble" [class.bubble--member]="m.role === 'member'" [class.bubble--companion]="m.role === 'companion'"
           [class.bubble--safety]="m.kind === 'safety'" [class.bubble--pending]="m.status === 'pending'">
        @if (m.status === 'pending') {
          {{ 'companion.chat.thinking' | translate }}
        } @else if (m.status === 'failed') {
          {{ ('companion.chat.failed.' + (m.error_code || 'error')) | translate }}
          @if (m.retryable && !readOnly()) {
            <button cButton color="secondary" variant="outline" size="sm" class="ms-2" (click)="retry(m)">{{ 'companion.chat.retry' | translate }}</button>
          }
        } @else {
          {{ m.content }}
        }
        @if (m.kind === 'plan_draft' && m.plan && m.plan.status === 'draft') {
          <div class="cp-hint mt-1">{{ 'companion.chat.see_plan' | translate }}</div>
        }

        <!-- The editable preview of understood values -->
        @if (m.preview_status === 'pending' && m.preview?.length) {
          <div class="preview">
            <div class="fw-semibold mb-1">{{ 'companion.preview.title' | translate }}</div>
            <div class="cp-hint mb-2">{{ 'companion.preview.hint' | translate }}</div>
            @for (it of previewItems(m); track $index; let i = $index) {
              <div class="preview__row" [class.preview__row--unclear]="it.state === 'unclear'">
                <div>
                  <div class="fw-semibold">{{ ('companion.kinds.' + it.kind) | translate }}</div>
                  <div class="cp-hint">{{ it.reference_date | date:'mediumDate':undefined:lang() }}</div>
                </div>
                <div>
                  @if (it.state === 'skipped') {
                    <span class="cp-muted">{{ 'companion.preview.skipped' | translate }}</span>
                  } @else if (it.kind === 'blood_pressure') {
                    <span class="cp-row">
                      <input class="form-control form-control-sm" type="number" inputmode="decimal" [ngModel]="it.value" (ngModelChange)="updatePreviewItem(m, i, { value: $event })" [disabled]="readOnly()" />
                      /
                      <input class="form-control form-control-sm" type="number" inputmode="decimal" [ngModel]="it.value2" (ngModelChange)="updatePreviewItem(m, i, { value2: $event })" [disabled]="readOnly()" />
                      <span class="cp-muted">mmHg</span>
                    </span>
                  } @else if (isNumeric(it.kind)) {
                    <span class="cp-row">
                      <input class="form-control form-control-sm" type="number" inputmode="decimal" step="0.1" [ngModel]="it.value" (ngModelChange)="updatePreviewItem(m, i, { value: $event })" [disabled]="readOnly()" />
                      <span class="cp-muted">{{ scaleMax[it.kind] ? ('/ ' + scaleMax[it.kind]) : (it.unit || '') }}</span>
                    </span>
                  } @else {
                    <input class="form-control form-control-sm" type="text" [ngModel]="it.text" (ngModelChange)="updatePreviewItem(m, i, { text: $event })" [disabled]="readOnly()" />
                  }
                  @if (it.state === 'unclear' && it.question) {
                    <div class="cp-hint">{{ it.question }}</div>
                  }
                </div>
                <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="readOnly()" (click)="removePreviewItem(m, i)" [attr.aria-label]="'companion.preview.remove' | translate">✕</button>
              </div>
            }
            @if (!readOnly()) {
              <div class="cp-actions">
                <button cButton color="primary" size="sm" [disabled]="busy()" (click)="savePreview(m)">{{ 'companion.preview.save' | translate }}</button>
                <button cButton color="secondary" variant="ghost" size="sm" [disabled]="busy()" (click)="discardPreview(m)">{{ 'companion.preview.discard' | translate }}</button>
              </div>
            }
          </div>
        }
        <div class="bubble__meta">{{ m.created_at | date:'short':undefined:lang() }}</div>
      </div>
    }
  </div>

  @if (!readOnly()) {
    <div class="compose">
      <textarea class="form-control" rows="2" [placeholder]="(aiOn() ? 'companion.chat.placeholder' : 'companion.chat.placeholder_ai_off') | translate"
                [ngModel]="text()" (ngModelChange)="text.set($event)" (keydown.control.enter)="send()" [disabled]="sending() || pending()"></textarea>
      <button cButton color="primary" [disabled]="sending() || pending() || !text().trim()" (click)="send()">{{ 'companion.chat.send' | translate }}</button>
    </div>
    <p class="cp-hint mt-1 mb-0">{{ 'companion.chat.hint' | translate }}</p>
  }
</div>

<!-- Quick form: every area as a reachable field -->
@if (!readOnly()) {
  <div class="cp-card">
    <div class="cp-row" style="justify-content: space-between">
      <h2 class="cp-h mb-0">{{ 'companion.form.title' | translate }}</h2>
      <button cButton color="secondary" variant="outline" size="sm" (click)="formOpen.set(!formOpen())">
        {{ (formOpen() ? 'companion.form.close' : 'companion.form.open') | translate }}
      </button>
    </div>
    @if (formSaved(); as n) { <p class="text-success mt-2 mb-0">{{ 'companion.form.saved' | translate:{ count: n } }}</p> }
    @if (formOpen()) {
      <p class="cp-hint mt-2">{{ 'companion.form.hint' | translate }}</p>

      <details class="form-section" open>
        <summary>{{ 'companion.form.sections.blood_pressure' | translate }}</summary>
        @for (r of bpReadings(); track $index; let i = $index) {
          <div class="form-grid">
            <div class="form-entry">
              <label>{{ 'companion.kinds.blood_pressure' | translate }} (mmHg)</label>
              <span class="cp-row">
                <input class="form-control" type="number" inputmode="numeric" placeholder="120" [ngModel]="r.value" (ngModelChange)="setBp(i, { value: $event })" />
                /
                <input class="form-control" type="number" inputmode="numeric" placeholder="80" [ngModel]="r.value2" (ngModelChange)="setBp(i, { value2: $event })" />
              </span>
            </div>
            <div class="form-entry">
              <label>{{ 'companion.kinds.heart_rate' | translate }} (bpm)</label>
              <input class="form-control" type="number" inputmode="numeric" [ngModel]="r.pulse" (ngModelChange)="setBp(i, { pulse: $event })" />
            </div>
            <div class="form-entry">
              <label>{{ 'companion.form.context' | translate }}</label>
              <select class="form-select" [ngModel]="r.context" (ngModelChange)="setBp(i, { context: $event })">
                <option value="rest">{{ 'companion.form.contexts.rest' | translate }}</option>
                <option value="after_exercise">{{ 'companion.form.contexts.after_exercise' | translate }}</option>
                <option value="unknown">{{ 'companion.form.contexts.unknown' | translate }}</option>
              </select>
            </div>
          </div>
        }
        <button type="button" class="btn btn-sm btn-link px-0 mt-1" (click)="addBpReading()">+ {{ 'companion.form.add_reading' | translate }}</button>
      </details>

      <details class="form-section" open>
        <summary>{{ 'companion.form.sections.body' | translate }}</summary>
        <div class="form-grid">
          @for (e of form(); track e.kind) {
            @if (['weight', 'sleep_hours', 'sleep_quality', 'wellbeing', 'mood', 'energy', 'stress'].includes(e.kind)) {
              <div class="form-entry">
                <label>{{ ('companion.kinds.' + e.kind) | translate }} <span class="cp-muted">{{ unitFor(e.kind) }}</span>
                  @if (scaleMax[e.kind]) { <span class="cp-muted">{{ ('companion.scales.' + e.kind) | translate }}</span> }
                </label>
                @if (scaleMax[e.kind]) {
                  <div class="scale">
                    @for (n of range(scaleMax[e.kind]); track $index; let idx = $index) {
                      <button type="button" [class.on]="e.value === (idx + 1) + ''" (click)="setScale(e, idx + 1)">{{ idx + 1 }}</button>
                    }
                  </div>
                } @else {
                  <input class="form-control" type="number" inputmode="decimal" step="0.1" [ngModel]="e.value" (ngModelChange)="setEntry(e, { value: $event, skipped: false })" />
                }
                <label class="cp-hint"><input type="checkbox" class="form-check-input me-1" [checked]="e.skipped" (change)="setEntry(e, { skipped: !e.skipped, value: '' })" />{{ 'companion.form.not_recorded' | translate }}</label>
              </div>
            }
          }
        </div>
      </details>

      <details class="form-section">
        <summary>{{ 'companion.form.sections.intake' | translate }}</summary>
        <div class="form-grid">
          @for (e of form(); track e.kind) {
            @if (['supplement', 'hydrogen_water', 'food', 'drink'].includes(e.kind)) {
              <div class="form-entry">
                <label>{{ ('companion.kinds.' + e.kind) | translate }} <span class="cp-muted">{{ unitFor(e.kind) }}</span></label>
                @if (e.kind === 'hydrogen_water') {
                  <input class="form-control" type="number" inputmode="decimal" [ngModel]="e.value" (ngModelChange)="setEntry(e, { value: $event, skipped: false })" />
                } @else {
                  <input class="form-control" type="text" [placeholder]="('companion.form.placeholders.' + e.kind) | translate" [ngModel]="e.text" (ngModelChange)="setEntry(e, { text: $event, skipped: false })" />
                }
                <label class="cp-hint"><input type="checkbox" class="form-check-input me-1" [checked]="e.skipped" (change)="setEntry(e, { skipped: !e.skipped, value: '', text: '' })" />{{ 'companion.form.not_recorded' | translate }}</label>
              </div>
            }
          }
        </div>
      </details>

      <details class="form-section">
        <summary>{{ 'companion.form.sections.movement' | translate }}</summary>
        <div class="form-grid">
          @for (e of form(); track e.kind) {
            @if (['exercise', 'reflection'].includes(e.kind)) {
              <div class="form-entry">
                <label>{{ ('companion.kinds.' + e.kind) | translate }}</label>
                <input class="form-control" type="text" [placeholder]="('companion.form.placeholders.' + e.kind) | translate" [ngModel]="e.text" (ngModelChange)="setEntry(e, { text: $event, skipped: false })" />
                <label class="cp-hint"><input type="checkbox" class="form-check-input me-1" [checked]="e.skipped" (change)="setEntry(e, { skipped: !e.skipped, text: '' })" />{{ 'companion.form.not_recorded' | translate }}</label>
              </div>
            }
          }
        </div>
      </details>

      <div class="cp-actions">
        <button cButton color="primary" [disabled]="busy()" (click)="submitForm()">{{ 'companion.form.save' | translate }}</button>
      </div>
    }
  </div>
}
